import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import { DatePicker } from '@material-ui/pickers'
import moment, { Moment } from 'moment'
import {
  Container,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  makeStyles,
} from '@material-ui/core'
import AppBar from '@material-ui/core/AppBar'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Box from '@material-ui/core/Box'

import { ERRORS, STRINGS } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'
import { COLORS } from 'styles'

import { Users } from './Users'
import { CouponsCard as BenefitsCard } from './Benefits'
import { RewardsTopFive } from './Rewards'
import { Charts } from './Charts/index'
import { downloadData } from './api'
import { Invoices } from './Invoices'
import { UserTests } from './UserTests'
import { Parking } from './Parking'
import { ParkingBenefits } from './ParkingBenefits'
import { DataResponse, DataParams, DataCards } from './types'
import { StoresTopFive } from './Stores'
import { GetStoresResponse, Store } from '../Stores/types'
import { getStores } from '../Stores/api'
import { CouponsCard, CouponsTopFive } from './Coupons'

const useStyles = makeStyles({
  tabsBar: {
    backgroundColor: COLORS.WHITE,
    color: COLORS.PRIMARY,
    boxShadow: 'none',
    width: '100%',
  },
})

const MONTHS_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

const dataSelect = {
  12: '\u00DAltimos 30 d\u00EDas',
  13: '\u00DAltimos 7 d\u00EDas',
}

interface TabPanelProps {
  children?: React.ReactNode
  index: any
  value: any
}

interface Items {
  [key: string]: string
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ width: `100%` }}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

const dataTest: DataCards = {
  visits: { amount: 0, avg: 0, unique: 0, amount_previous: 0, avg_previous: 0, unique_previous: 0 },
  coupons: { used: 0, avg: 0, used_previous: 0, avg_previous: 0 },
  benefits: { used: 0, avg: 0, used_previous: 0, avg_previous: 0 },
  rewards: { used: 0, avg: 0, used_previous: 0, avg_previous: 0 },
  users: { 
    active: 0, 
    created: 0, 
    active_previous: 0, 
    created_previous: 0,
    level_1: 0,
    level_1_previous: 0,
    level_1_birthday: 0,
    level_1_birthday_previous: 0,
    level_2: 0,
    level_2_previous: 0,
    level_2_birthday: 0,
    level_2_birthday_previous: 0,
    level_3: 0,
    level_3_previous: 0,
    level_3_birthday: 0,
    level_3_birthday_previous: 0,
  },
  invoices: {
    created: 0,
    created_previous: 0,
    amount: 0,
    amount_previous: 0,
    average_amount: 0,
    average_amount_previous: 0,
  },
  userTests: { completed: 0, completed_previous: 0 },
  ga_reports: {
    current: { active1DayUsers: 0, active28DayUsers: 0, active7DayUsers: 0 },
    previous: { active1DayUsers: 0, active28DayUsers: 0, active7DayUsers: 0 },
  },
  parking: {
    payments: 0,
    payments_previous: 0,
    total_amount: 0,
    total_amount_previous: 0,
    // nuevos campos
    tickets: 0,
    tickets_previous: 0,
    minutes: 0,
    minutes_previous: 0,
  },
}

const Dashboard: React.FC = () => {
  const classes = useStyles()
  const [startDate, setStartDate] = useState<Moment>(
    moment()
      .subtract(7, 'days')
      .startOf('day'),
  )
  const [endDate, setEndDate] = useState<Moment>(
    moment()
      .subtract(1, 'days')
      .endOf('day'),
  )
  const [dataCards, setDataCards] = useState<DataCards>(dataTest)
  const [periodNumber, setPeriodNumber] = useState<number>(13)
  const [isMonth, setIsMonth] = useState(false)
  const [storeSelected, setStoreSelected] = useState<number>(0)
  const [value, setValue] = React.useState(0)

  const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []

  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    getStoresResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const setPeriod = async (value: number) => {
    const current = moment()
    dataCards.period = undefined

    if (value < 12) {
      current.set('month', value)
      setStartDate(
        current
          .clone()
          .startOf('month')
          .startOf('day'),
      )
      setEndDate(
        current
          .clone()
          .endOf('month')
          .endOf('day'),
      )
      setIsMonth(true)
    }

    if (value === 12) {
      const thirtyDatesPrevious = current.clone().subtract(30, 'days')
      setStartDate(thirtyDatesPrevious.startOf('day'))
      setEndDate(current.endOf('day').subtract(1, 'days'))
      setIsMonth(false)
    }

    if (value === 13) {
      const sevenDatesPrevious = current.clone().subtract(7, 'days')
      setStartDate(sevenDatesPrevious.startOf('day'))
      setEndDate(current.endOf('day').subtract(1, 'days'))
      setIsMonth(false)
    }
  }

  const handlePeriodSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPeriod = event.target.value as number
    setPeriodNumber(newPeriod)
    setPeriod(newPeriod)
  }

  const handleStartDateChange = (newDate: Moment | null) => {
    if (newDate) {
      setStartDate(newDate.startOf('day'))
      setIsMonth(false)
      // Si la fecha seleccionada es mayor que endDate, ajustar endDate autom?ticamente
      if (endDate && newDate.isAfter(endDate)) {
        setEndDate(newDate.clone().endOf('day'))
      }
    }
  }

  const handleEndDateChange = (newDate: Moment | null) => {
    if (newDate) {
      setEndDate(newDate.endOf('day'))
      setIsMonth(false)
    }
  }

  const handleStoreSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedValue = Number(event.target.value)
    setStoreSelected(selectedValue)
  }

  const [requestData, isRequestingData] = useApiCall<DataParams, DataResponse>(downloadData)

  const handleDownload = async () => {
    try {
      const data = await requestData({
        storeId: storeSelected,
        startDate: startDate?.toISOString(true),
        endDate: endDate?.toISOString(true),
        ...(isMonth ? { month: periodNumber } : {}),
      })
      setDataCards({ ...dataTest, ...data.data })
    } catch (err) {
      console.error('Error al descargar datos:', err)
      snackbar.show('No se pudo editar la promo. Intente de nuevo.')
    }
  }

  const getPreviousMonths = (): Items => {
    const currentMonth = new Date().getMonth()
    const previousMonths: Items = {}

    for (let index = 0; index < currentMonth; index++) {
      previousMonths[String(index)] = MONTHS_ES[index]
    }

    return previousMonths
  }

  const listItems = () => {
    const previousMonths = getPreviousMonths()
    const items: Items = { ...previousMonths, ...dataSelect }

    return Object.keys(items)
      .map((key, index) => (
        <MenuItem key={`list_${index}`} value={key}>
          {items[key]}
        </MenuItem>
      ))
      .reverse()
  }

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue)
  }

  return (
    <Container>
      <Grid container spacing={3}>
        {/* show filters also on charts */}
        {(value === 0 || value === 2) && (
          <>
            <Grid item xs={12} sm={3}>
              <Select value={periodNumber} onChange={handlePeriodSelect} fullWidth>
                {listItems()}
              </Select>
              <Select
                value={storeSelected}
                onChange={handleStoreSelect}
                fullWidth
                defaultValue={0}
                placeholder="Seleccionar tienda"
              >
                <MenuItem value={0}>Todas las tiendas</MenuItem>
                {stores.map(store => (
                  <MenuItem key={store.id} value={Number(store.id)}>
                    {store.id} - {store.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                autoOk
                orientation="landscape"
                format={STRINGS.DATE_FORMAT}
                style={{ width: '100%' }}
                label="Desde"
                disableFuture={false}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                autoOk
                orientation="landscape"
                format={STRINGS.DATE_FORMAT}
                style={{ width: '100%' }}
                label="Hasta"
                disableFuture={false}
                minDate={startDate}
                minDateMessage="La fecha no debe ser anterior a la fecha inicial"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                onClick={handleDownload}
                variant="contained"
                color="primary"
                disabled={isRequestingData || startDate > endDate}
              >
                Generar{' '}
                {isRequestingData && (
                  <CircularProgress
                    style={{ color: 'gray', marginLeft: 10 }}
                    thickness={5}
                    size={20}
                  />
                )}
              </Button>
            </Grid>
          </>
        )}

        <AppBar position="static" color="inherit" elevation={0} className={classes.tabsBar}>
          <Tabs
            value={value}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="KPIs" {...a11yProps(0)} />
            <Tab label="Promos, Beneficios y Rewards" {...a11yProps(1)} />
            <Tab label="Parking beneficios" {...a11yProps(2)} />
          </Tabs>
        </AppBar>

        <TabPanel value={value} index={0}>
          <Grid container spacing={3} item xs={12}>
            {/* <Visits data={dataCards.visits} /> */}
            <CouponsCard dataCoupons={dataCards.coupons} />
            <Users data={dataCards.users} />
            <Invoices data={dataCards.invoices} />
            <BenefitsCard dataBenefits={dataCards.benefits} dataRewards={dataCards.rewards} />
            <UserTests data={dataCards.userTests} />
            <Parking data={dataCards.parking} />
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Grid container spacing={3} item xs={12}>
            <CouponsTopFive startDate={startDate} endDate={endDate} storeId={storeSelected} />
            <RewardsTopFive startDate={startDate} endDate={endDate} storeId={storeSelected} />
            <StoresTopFive startDate={startDate} endDate={endDate} storeId={storeSelected} />
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={2}>
          <ParkingBenefits startDate={startDate} endDate={endDate} />
        </TabPanel>

        <TabPanel value={value} index={3}>
          <Charts startDate={startDate} endDate={endDate} />
        </TabPanel>
      </Grid>
    </Container>
  )
}

export { Dashboard }
